import Addicon from "@mui/icons-material/Add";
import { CircularProgress, Box } from "@mui/material";
import { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import DeleteIcon from "@mui/icons-material/DeleteForever";
import WarehouseSearchInput, {
  getWarehouseIdFilterResult,
} from "$forms/WarehouseSearchInput";

import { Button, Modal, Popin, Table, Text } from "$components";
import GenericSelect from "$forms/GenericSelect";
import useJStyles from "$hooks/useJStyles";
import useAuth from "$hooks/useAuth";
import useWMSConfigForm from "$hooks/useWMSConfigForm";
import WMSConfigEnum, {
  MonStockConfigType,
  StockItConfigType,
  WMSConfigType,
} from "$types/WMSConfig";
import useFilters from "$hooks/useFilters";
import useWMSConfig from "$hooks/useWMSConfig";
import { theme } from "$utils/theme/bataillog";
import WMSConfigForm from "./WMSConfigForm";
import { EntrepotService } from "$services";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";

enum ERROR_MSG {
  NO_CONFIGS = "Vous n’avez pas encore créé de configurations WMS",
  NO_RESULTS = "Aucun résultat ne correspond à votre recherche",
  LOADING = "Chargement en cours",
}

function WMSConfigList() {
  const { isAdmin } = useAuth();
  const { jclasses } = useJStyles();
  const { filters, setFilter } = useFilters("WMSconfig-page");
  const { wmsConfigList, loading, refreshPageTable, setRefreshPageTable } =
    useWMSConfig();
  const {
    togglePopin,
    handleSubmit,
    handleDelete,
    isOpened,
    submitting,
    error,
  } = useWMSConfigForm();
  const [wmsConfig, setWMSConfig] =
    useState<WMSConfigType<StockItConfigType | MonStockConfigType>>(null);
  const [confirmModal, setConfirmModal] = useState(false);
  const [warehouses, setWarehouses] = useState([]);

  const filteredConfigs = useMemo(() => {
    let configs = wmsConfigList || [];
    if (filters.type) {
      configs = configs.filter((config) => config.type === filters.type);
    }
    if (filters.warehouses) {
      configs = getWarehouseIdFilterResult(
        configs,
        filters.warehouses,
        warehouses
      );
    }
    return configs;
  }, [wmsConfigList, filters, warehouses]);

  // get warehouses
  const fecthWarehouses = useCallback(async () => {
    if (filteredConfigs?.length > 0) {
      // Get all ids
      const listWarehouseId = [
        ...new Set(
          filteredConfigs
            .map((conf: any) => conf.warehouses.map((s) => s))
            .flat()
            .filter((f) => f)
        ),
      ];
      const filters = {
        $inId: listWarehouseId,
      };

      const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
      let getWarehouses: any = await queryClient.getQueryData(queryKey);
      if (!getWarehouses) {
        getWarehouses = await queryClient.fetchQuery(
          queryKey,
          () => EntrepotService.getEntrepots(filters),
          { cacheTime: 5 * 60 * 1000 }
        );
        queryClient.setQueryData(queryKey, getWarehouses);
      }
      setWarehouses(getWarehouses?.data || []);
    }
  }, [filteredConfigs]);

  useEffect(() => {
    fecthWarehouses();
  }, [fecthWarehouses]);

  const tHeadFilters = [
    {
      id: "type",
      label: (
        <div className={jclasses.jTopHead}>
          <GenericSelect
            label="Type"
            onChange={(e: any) => {
              setFilter("type", e);
              setRefreshPageTable(true);
            }}
            value={filters.type}
            options={[
              {
                value: WMSConfigEnum.MonStock,
                label: WMSConfigEnum.MonStock,
              },
              {
                value: WMSConfigEnum.StockIt,
                label: WMSConfigEnum.StockIt,
              },
            ]}
          />
        </div>
      ),
    },
    {
      id: "warehouses",
      label: (
        <div className={jclasses.jTopHead}>
          <WarehouseSearchInput
            value={filters.warehouses}
            label="Entrepôts"
            onChange={(e: any) => {
              setFilter("warehouses", e.target.value);
              setRefreshPageTable(true);
            }}
          />
        </div>
      ),
    },
    { id: "action", label: "", width: 60 },
  ];

  const bodyTable = filteredConfigs?.map((config) => {
    const renderCell = (value: ReactNode, onClickAction?: () => void) => ({
      onClickAction:
        onClickAction ||
        (() => {
          setWMSConfig(config);
          togglePopin(true);
        }),
      value,
    });

    return [
      renderCell(
        <Text className={jclasses.jEllipsedText} type="important">
          {config.type || ""}
        </Text>
      ),
      renderCell(
        <Text>
          {config?.warehouses?.length > 1
            ? "Multiple"
            : warehouses.find((e: any) => e._id === config.warehouses[0])
                ?.name || ""}
        </Text>
      ),
      renderCell(
        <Box textAlign="center" width="100%">
          <DeleteIcon style={{ color: theme.color.blueLight }} />
        </Box>,

        () => {
          setWMSConfig(config);
          setConfirmModal(true);
        }
      ),
    ];
  });

  const handleDeleteConfig = () => {
    handleDelete(wmsConfig._id);
    setConfirmModal(false);
  };

  return (
    <>
      {/* Header buttons */}
      <div className={jclasses.jBeforePaper}>
        <div className={jclasses.jBeforePaper_filter}>
          {isAdmin() && (
            <div>
              <Button
                variant="outlined"
                onClick={() => {
                  setWMSConfig(null);
                  togglePopin(true);
                }}
              >
                <Addicon />
                Créer une configuration
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      {/* Relative for the no results placeholder */}
      <div style={{ position: "relative" }}>
        <Table
          bodyData={bodyTable}
          rowsPerPage={50}
          thead={tHeadFilters}
          pageName="WMSConfigList"
          refreshPage={refreshPageTable}
          usePaper={false}
        />
        {!bodyTable?.length && (
          <div className={jclasses.jNoResultPlaceHolder}>
            {!loading && !bodyTable?.length && (
              <Text type="h3">
                {WMSConfigList?.length
                  ? ERROR_MSG.NO_RESULTS
                  : ERROR_MSG.NO_CONFIGS}
              </Text>
            )}
            {loading && !bodyTable?.length && (
              <div className={jclasses.jLoadingTable}>
                <Text type="h3">{ERROR_MSG.LOADING}</Text>
                <CircularProgress color="inherit" />
              </div>
            )}
          </div>
        )}
      </div>
      <Popin openPopin={isOpened} toggleDrawer={togglePopin}>
        <WMSConfigForm
          wmsConfig={wmsConfig}
          onSubmit={handleSubmit}
          onReset={() => togglePopin(false)}
          isSubmitting={submitting}
          wmsConfigError={error}
        />
      </Popin>
      <Modal
        open={confirmModal}
        onCancel={() => setConfirmModal(false)}
        title="Suppression de la configuration WMS"
        textValidate="Oui"
        textCancel="Non"
        onConfirm={handleDeleteConfig}
      >
        <div>
          Vous êtes sur le point de supprimer une configuration WMS.
          <br />
          Êtes-vous sûr de vouloir continuer ?
        </div>
      </Modal>
    </>
  );
}

export default WMSConfigList;
